import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withKeys as _withKeys, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vShow as _vShow, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["id", "onKeydown", "aria-invalid", "aria-describedby"]
const _hoisted_2 = { class: "searchable-select__dropdown-value" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "searchable-select__dropdown-arrow" }, "▼", -1)
const _hoisted_4 = ["name", "onKeydown", "placeholder", "aria-invalid", "aria-describedby"]
const _hoisted_5 = { class: "searchable-select__dropdown-options" }
const _hoisted_6 = ["onMousedown", "onMouseenter"]
const _hoisted_7 = {
  key: 0,
  class: "searchable-select__no-results"
}
const _hoisted_8 = {
  key: 1,
  class: "searchable-select__more-results"
}
const _hoisted_9 = {
  key: 2,
  class: "searchable-select__hint"
}
const _hoisted_10 = ["for"]
const _hoisted_11 = {
  key: 0,
  class: "form__indicator"
}
const _hoisted_12 = ["id"]

import { FormOption } from "@/types/formOption";
import { validate, Status } from '@/validation'
import { required, Rule } from '@/validation/rules'
import { ref, watch, computed, nextTick } from "vue";
import { useI18n } from "vue3-i18n";

// eslint-disable-next-line

export default /*#__PURE__*/_defineComponent({
  __name: 'FormSearchableSelect',
  props: {
    name: {},
    modelValue: {},
    label: {},
    rules: {},
    options: {},
    noLabel: { type: Boolean },
    maxDisplayed: {}
  },
  emits: ["update:modelValue", "validated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// eslint-disable-next-line
__expose({
  validateInput
});

// eslint-disable-next-line
const emit = __emit;

const { t } = useI18n();
const status = ref<Status>({ valid: true });
const isRequired = !(props.rules != null && props.rules.length == 0);
const inputValue = ref<string>(props.modelValue as string);
const searchTerm = ref("");
const isOpen = ref(false);
const highlightedIndex = ref(0);
const maxDisplayed = props.maxDisplayed || 100;
const searchInput = ref<HTMLInputElement | null>(null);

// Filter options based on search term
const filteredOptions = computed(() => {
  if (!searchTerm.value) {
    return props.options.slice(0, maxDisplayed);
  }

  const term = searchTerm.value.toLowerCase();
  const filtered: FormOption[] = [];

  // Fast filtering - stop after we find enough results
  for (let i = 0; i < props.options.length && filtered.length < maxDisplayed * 2; i++) {
    const option = props.options[i];
    if (option.label.toLowerCase().includes(term) ||
      option.name.toLowerCase().includes(term)) {
      filtered.push(option);
    }
  }

  return filtered;
});

// Limit displayed options
const displayedOptions = computed(() =>
  filteredOptions.value.slice(0, maxDisplayed)
);

const hasMoreResults = computed(() =>
  filteredOptions.value.length > maxDisplayed
);

// Get label for selected value
const selectedLabel = computed(() => {
  if (!inputValue.value) return "";
  const selected = props.options.find(opt => opt.name === inputValue.value);
  return selected?.label || "";
});

watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue;
});

watch(searchTerm, () => {
  highlightedIndex.value = 0;
});

async function openDropdown() {
  isOpen.value = true;
  await nextTick();
  searchInput.value?.focus();
}

function selectOption(option: FormOption) {
  inputValue.value = option.name;
  searchTerm.value = "";
  isOpen.value = false;
  handleChange();
}

function navigateOptions(direction: number) {
  if (!isOpen.value || displayedOptions.value.length === 0) return;

  highlightedIndex.value = Math.max(
    0,
    Math.min(displayedOptions.value.length - 1, highlightedIndex.value + direction)
  );
}

function selectHighlighted() {
  if (isOpen.value && displayedOptions.value[highlightedIndex.value]) {
    selectOption(displayedOptions.value[highlightedIndex.value]);
  }
}

function closeDropdown() {
  isOpen.value = false;
  searchTerm.value = "";
}

function handleChange() {
  validateInput();
  emit("update:modelValue", inputValue.value);
}

function handleBlur() {
  setTimeout(() => {
    isOpen.value = false;
    searchTerm.value = "";
    validateInput();
  }, 200);
}

function validateInput() {
  let validationRules = props.rules ? props.rules : [required]
  status.value = validate(inputValue.value, validationRules)
  emit("validated", props.name, status.value);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["form__field", { error: !status.value.valid }])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["searchable-select", { 'is-open': isOpen.value }])
    }, [
      (!isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "searchable-select__dropdown",
            id: _ctx.name,
            onClick: openDropdown,
            tabindex: "0",
            onKeydown: [
              _withKeys(openDropdown, ["enter"]),
              _withKeys(_withModifiers(openDropdown, ["prevent"]), ["space"])
            ],
            "aria-invalid": !status.value.valid,
            "aria-describedby": `error__${_ctx.name}`
          }, [
            _createElementVNode("span", _hoisted_2, _toDisplayString(selectedLabel.value || _unref(t)('search.typeToSearch')), 1),
            _hoisted_3
          ], 40, _hoisted_1))
        : _createCommentVNode("", true),
      (isOpen.value)
        ? _withDirectives((_openBlock(), _createElementBlock("input", {
            key: 1,
            ref_key: "searchInput",
            ref: searchInput,
            type: "text",
            name: _ctx.name,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
            onBlur: handleBlur,
            onKeydown: [
              _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (navigateOptions(1)), ["prevent"]), ["down"])),
              _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (navigateOptions(-1)), ["prevent"]), ["up"])),
              _withKeys(_withModifiers(selectHighlighted, ["prevent"]), ["enter"]),
              _withKeys(closeDropdown, ["escape"])
            ],
            placeholder: _unref(t)('search.typeToSearch'),
            "aria-invalid": !status.value.valid,
            "aria-describedby": `error__${_ctx.name}`,
            autocomplete: "off",
            class: "searchable-select__input"
          }, null, 40, _hoisted_4)), [
            [_vModelText, searchTerm.value]
          ])
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("ul", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedOptions.value, (option, index) => {
          return (_openBlock(), _createElementBlock("li", {
            key: option.name,
            class: _normalizeClass({ highlighted: index === highlightedIndex.value }),
            onMousedown: _withModifiers(($event: any) => (selectOption(option)), ["prevent"]),
            onMouseenter: ($event: any) => (highlightedIndex.value = index)
          }, _toDisplayString(option.label), 43, _hoisted_6))
        }), 128)),
        (filteredOptions.value.length === 0 && searchTerm.value)
          ? (_openBlock(), _createElementBlock("li", _hoisted_7, _toDisplayString(_unref(t)('search.noResults')), 1))
          : _createCommentVNode("", true),
        (hasMoreResults.value)
          ? (_openBlock(), _createElementBlock("li", _hoisted_8, _toDisplayString(_unref(t)('search.numberOfResultsDisplayed').replace('{0}', displayedOptions.value.length.toString()).replace('{1}', filteredOptions.value.length.toString())), 1))
          : _createCommentVNode("", true),
        (!searchTerm.value && _ctx.options.length > _unref(maxDisplayed))
          ? (_openBlock(), _createElementBlock("li", _hoisted_9, _toDisplayString(_unref(t)('search.typeToSearchThroughOptions').replace('{0}', _ctx.options.length.toString())), 1))
          : _createCommentVNode("", true)
      ], 512), [
        [_vShow, isOpen.value]
      ])
    ], 2),
    (!_ctx.noLabel)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: _ctx.name
        }, [
          _createTextVNode(_toDisplayString(_ctx.label ? _ctx.label : _ctx.name) + " ", 1),
          isRequired
            ? (_openBlock(), _createElementBlock("span", _hoisted_11, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_10))
      : _createCommentVNode("", true),
    (!status.value.valid)
      ? (_openBlock(), _createElementBlock("span", {
          key: 1,
          class: "form__error-message",
          id: `error__${_ctx.name}`
        }, _toDisplayString(_unref(t)('validation.select')), 9, _hoisted_12))
      : _createCommentVNode("", true)
  ], 2))
}
}

})